// src/components/Home.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/Home.css";
import "../styles/Landing.css";

const Icon = ({ size = 32, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const CONDITIONS = [
  {
    id: "enlarged-prostate",
    name: "Enlarged prostate (BPH)",
    symptoms: "Frequent urination, weak stream, night waking",
    icon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />,
  },
  {
    id: "overactive-bladder",
    name: "Overactive bladder",
    symptoms: "Urgency, leakage, sudden need to go",
    icon: (
      <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7Z" />
    ),
  },
  {
    id: "erectile-dysfunction",
    name: "Erectile dysfunction",
    symptoms: "Difficulty getting or keeping an erection",
    icon: (
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5Z" />
    ),
  },
  {
    id: "recurrent-utis",
    name: "Recurrent UTIs",
    symptoms: "Repeat infections, burning, pelvic pressure",
    icon: (
      <>
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1Z" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </>
    ),
  },
  {
    id: "recurrent-kidney-stones",
    name: "Kidney stones",
    symptoms: "Prevention, recurrence, post-passage follow-up",
    icon: (
      <>
        <path d="M6 3h12l4 6-10 13L2 9Z" />
        <path d="M11 3 8 9l4 13 4-13-3-6" />
        <path d="M2 9h20" />
      </>
    ),
  },
  {
    id: "low-testosterone",
    name: "Low testosterone",
    symptoms: "Fatigue, low libido, lab-confirmed treatment",
    icon: (
      <>
        <path d="m12 14 4-4" />
        <path d="M3.34 19a10 10 0 1 1 17.32 0" />
      </>
    ),
  },
  {
    id: "premature-ejaculation",
    name: "Premature ejaculation",
    symptoms: "Behavioral and prescription options",
    icon: (
      <>
        <path d="M10 2h4" />
        <path d="M12 14l3-3" />
        <circle cx="12" cy="14" r="8" />
      </>
    ),
  },
];

const STEPS = [
  {
    title: "Take the symptom assessment",
    text: "A validated questionnaire scores how much your symptoms are affecting you, and points to the right care pathway.",
  },
  {
    title: "Meet your urology-trained provider",
    text: "A video visit within days — no referral, no waiting room. They review your score, history, and any labs.",
  },
  {
    title: "Start your plan, prescription sent",
    text: "Prescriptions sent to your pharmacy. Exercises, fluid logs, and follow-ups all live in one plan.",
  },
  {
    title: "Track your score, adjust together",
    text: "Re-score whenever things change. Your provider sees the trend and updates treatment accordingly.",
  },
];

const REASONS = [
  {
    title: "Urology-trained providers",
    text: "Not general telehealth. The provider reading your score treats these conditions every day, and follows evidence-based pathways for each one.",
    icon: (
      <>
        <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
        <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
        <circle cx="20" cy="10" r="2" />
      </>
    ),
  },
  {
    title: "No referral, no wait list",
    text: "Start with the assessment and book directly. You don't need a primary-care visit first, and you're not queued behind a clinic schedule.",
    icon: (
      <>
        <path d="M8 2v4" />
        <path d="M16 2v4" />
        <rect width="18" height="18" x="3" y="4" rx="2" />
        <path d="M3 10h18" />
        <path d="m9 16 2 2 4-4" />
      </>
    ),
  },
  {
    title: "A score you can actually watch",
    text: "Your symptom score is re-taken whenever things change, so you and your provider are looking at a trend instead of a memory.",
    icon: (
      <>
        <path d="M3 3v16a2 2 0 0 0 2 2h16" />
        <path d="m19 9-5 5-4-4-3 3" />
      </>
    ),
  },
  {
    title: "One plan, entirely online",
    text: "Prescriptions, exercises, and logs in one place — with the prescription sent straight to your pharmacy after the visit.",
    icon: (
      <>
        <path d="M2 9V5a2 2 0 0 1 2-2h3.5L9 5h11a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2Z" />
        <path d="M12 11v6" />
        <path d="M9 14h6" />
      </>
    ),
  },
];

// Places patients can be seen from. All in South Carolina; see the section below.
const SC_AREAS = [
  "Charleston",
  "Columbia",
  "Greenville",
  "Myrtle Beach",
  "Spartanburg",
  "Rock Hill",
  "Summerville",
  "Mount Pleasant",
  "Florence",
  "Hilton Head Island",
  "Aiken",
  "Anderson",
];

const Eyebrow = ({ children }) => (
  <div className="landing-eyebrow">
    <span className="landing-eyebrow-rule" />
    {children}
  </div>
);

const Check = ({ done, small }) => (
  <span
    className={
      "landing-check" +
      (done ? " landing-check-done" : "") +
      (small ? " landing-check-small" : "")
    }
  />
);

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="home-page-root landing-root">
      {/* Top nav */}
      <header className="home-nav">
        <Link to="/" className="home-nav-left" aria-label="Youro home">
          <div className="home-nav-logo-circle">Y</div>
          <span className="home-nav-logo-text">Youro</span>
        </Link>
        <nav className="home-nav-center">
          <button
            className="home-nav-link"
            onClick={() => navigate("/about")}
          >
            About
          </button>
          <button
            className="home-nav-link"
            onClick={() => navigate("/conditions")}
          >
            Find Your Diagnosis
          </button>
          <button
            className="home-nav-link"
            onClick={() => navigate("/assessment")}
          >
            Symptom Assessment
          </button>
        </nav>
        <div className="home-nav-right">
          <button
            className="home-nav-signin"
            onClick={() => navigate("/login")}
          >
            Sign In
          </button>
          {/* Outlined here: the hero already carries this page's Coral CTA */}
          <button
            className="home-nav-get-started landing-nav-get-started"
            onClick={() => navigate("/signup")}
          >
            Get Started
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="landing-hero">
        <div className="landing-hero-copy">
          <Eyebrow>Urology, personalized</Eyebrow>
          <h1 className="landing-hero-title">
            Urology care,{" "}
            <span className="landing-hero-highlight">from actual urologists</span>.
          </h1>
          <p className="landing-hero-text">
            Skip the referral and the waiting room — get seen by a
            urology-trained provider this week. Track your symptoms, understand
            your treatment, and stay involved in your care, all from your phone.
          </p>
          <div className="landing-actions">
            <button
              className="landing-btn-primary"
              onClick={() => navigate("/signup")}
            >
              Start Your Care Journey <span aria-hidden="true">→</span>
            </button>
            <button
              className="landing-btn-secondary"
              onClick={() => navigate("/assessment")}
            >
              Take Symptom Assessment
            </button>
          </div>
          <ul className="landing-hero-points">
            <li><Check done />No referral needed</li>
            <li><Check done />Care from urology-trained providers</li>
            <li><Check done />Symptom tracking built in</li>
          </ul>
        </div>

        {/* Illustrative product preview, not real patient data */}
        <div className="landing-hero-visual" aria-hidden="true">
          <div className="landing-phone">
            <div className="landing-phone-header">
              <span className="landing-phone-title">Your dashboard</span>
              <span className="landing-phone-meta">Mon 8 Sep</span>
            </div>
            <div className="landing-score">
              <div className="landing-score-ring">
                <div className="landing-score-inner">
                  <div className="landing-score-value">72</div>
                  <div className="landing-score-of">of 100</div>
                </div>
              </div>
              <div>
                <div className="landing-phone-label">Symptom Score</div>
                <div className="landing-severity">Moderately Bothersome</div>
              </div>
            </div>
            <div className="landing-plan-header">
              <span className="landing-phone-label">Current Care Plan</span>
              <span className="landing-phone-meta">2 of 3 done</span>
            </div>
            <div className="landing-plan">
              <div><Check done />Tamsulosin 0.4 mg · delivered</div>
              <div><Check done />Pelvic floor set · 2× daily</div>
              <div><Check />Fluid log · today</div>
              <div className="landing-progress">
                <div className="landing-progress-bar" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="landing-section landing-section-card">
        <div className="landing-container">
          <div className="landing-section-intro landing-section-intro-center">
            <Eyebrow>Conditions we treat</Eyebrow>
            <h2 className="landing-section-title">Find your diagnosis.</h2>
            <p className="landing-section-text">
              Seven urologic conditions, each with a dedicated care pathway
              built by urology-trained providers. Not sure which fits? Start
              with the symptom assessment.
            </p>
          </div>

          <div className="landing-conditions-grid">
            {CONDITIONS.map((c) => (
              <Link
                key={c.id}
                to={`/conditions/${c.id}`}
                className="landing-condition"
              >
                <span className="landing-icon"><Icon>{c.icon}</Icon></span>
                <h3 className="landing-condition-title">{c.name}</h3>
                <p className="landing-condition-text">{c.symptoms}</p>
                <span className="landing-link">Learn more →</span>
              </Link>
            ))}
            <div className="landing-condition landing-condition-unsure">
              <h3 className="landing-condition-title">Not sure yet?</h3>
              <p className="landing-condition-text">
                Answer a few questions and we'll match you to the right care
                pathway.
              </p>
              <button
                className="landing-btn-primary landing-btn-small"
                onClick={() => navigate("/assessment")}
              >
                Take the assessment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="landing-section landing-section-sand">
        <div className="landing-container">
          <div className="landing-section-intro">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="landing-section-title">
              From first question to treatment, within days.
            </h2>
            <p className="landing-section-text">
              Everything happens on your phone — the assessment, the visit, the
              prescription, and the follow-up.
            </p>
          </div>

          <div className="landing-how-grid">
            {/* Illustrative visit preview */}
            <div className="landing-how-visual" aria-hidden="true">
              <div className="landing-phone landing-phone-narrow">
                <div className="landing-phone-header">
                  <span className="landing-phone-title">Your visit</span>
                  <span className="landing-phone-meta">Thu 11 Sep</span>
                </div>
                <div className="landing-visit-card">
                  <div className="landing-phone-label">Dr. Youro, MD</div>
                  <div className="landing-visit-meta">Urology · Video visit · 20 min</div>
                  <div className="landing-visit-note">
                    Reviewing your score of 72 and your fluid log before we start.
                  </div>
                </div>
                <div className="landing-visit-card landing-visit-card-last">
                  <div className="landing-phone-label">After your visit</div>
                  <div className="landing-visit-list">
                    <div><Check done small />Prescription sent to pharmacy</div>
                    <div><Check done small />Care plan updated</div>
                    <div><Check small />Re-score in 4 weeks</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <ol className="landing-steps">
                {STEPS.map((step, i) => (
                  <li key={step.title} className="landing-step">
                    <div className="landing-step-number">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="landing-step-title">{step.title}</h3>
                      <p className="landing-step-text">{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <button
                className="landing-btn-primary landing-steps-cta"
                onClick={() => navigate("/signup")}
              >
                Start Your Care Journey <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY YOURO */}
      <section className="landing-section landing-section-card">
        <div className="landing-container">
          <div className="landing-section-intro landing-section-intro-wide">
            <Eyebrow>Why Youro</Eyebrow>
            <h2 className="landing-section-title">
              Specialist care, built to keep going.
            </h2>
            <p className="landing-section-text">
              Most urologic symptoms don't resolve in a single visit. Youro is
              built around the follow-through — the score, the plan, and the
              provider who knows your history.
            </p>
          </div>

          <div className="landing-why-grid">
            {REASONS.map((r) => (
              <div key={r.title} className="landing-why-card">
                <span className="landing-icon"><Icon>{r.icon}</Icon></span>
                <div>
                  <h3 className="landing-why-title">{r.title}</h3>
                  <p className="landing-why-text">{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AREAS WE SERVE — South Carolina only. Youro's providers are licensed
          there and nowhere else yet, so these are places we can actually treat
          someone. Cities in neighbouring states stay off this list until that
          changes. */}
      <section className="landing-section landing-section-sand" aria-labelledby="areas-heading">
        <div className="landing-container">
          <div className="landing-section-intro landing-section-intro-wide">
            <Eyebrow>Areas we serve</Eyebrow>
            <h2 id="areas-heading" className="landing-section-title">
              Urology care anywhere in South Carolina.
            </h2>
            <p className="landing-section-text">
              Youro's providers are licensed in South Carolina, so you can see one
              from home wherever you are in the state. You'll need to be in South
              Carolina at the time of your appointment.
            </p>
          </div>
          <ul className="landing-areas">
            {SC_AREAS.map((place) => (
              <li key={place} className="landing-area">{place}</li>
            ))}
          </ul>
          <p className="landing-areas-note">…and everywhere else in South Carolina.</p>
        </div>
      </section>

      {/* CLOSE */}
      <section className="landing-close">
        <div className="landing-container">
          <h2 className="landing-section-title landing-close-title">
            Ready to start your care?
          </h2>
          <p className="landing-close-text">
            Personalized, urology-specific care from providers who treat these
            conditions every day — not general telehealth.
          </p>
          <div className="landing-actions landing-actions-center">
            <button
              className="landing-btn-primary"
              onClick={() => navigate("/signup")}
            >
              Get Started Today <span aria-hidden="true">→</span>
            </button>
            <button
              className="landing-btn-secondary"
              onClick={() => navigate("/assessment")}
            >
              Free Symptom Assessment
            </button>
          </div>
          <Link to="/pricing" className="landing-link landing-close-link">
            View Pricing →
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="landing-footer-grid">
            <div>
              <Link to="/" className="home-nav-left landing-footer-brand" aria-label="Youro home">
                <div className="home-nav-logo-circle">Y</div>
                <span className="home-nav-logo-text">Youro</span>
              </Link>
              <p className="landing-footer-text">
                Evidence-based urologic care from urology-trained providers,
                entirely online.
              </p>
              <div className="landing-footer-badge">
                Currently available in South Carolina
              </div>
            </div>
            <nav aria-label="Explore">
              <div className="landing-footer-heading">Explore</div>
              <ul className="landing-footer-links">
                <li><Link to="/about">About</Link></li>
                <li><Link to="/conditions">Find Your Diagnosis</Link></li>
                <li><Link to="/assessment">Symptom Assessment</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
              </ul>
            </nav>
            <nav aria-label="Support">
              <div className="landing-footer-heading">Support</div>
              <ul className="landing-footer-links">
                <li><a href="mailto:support@yourohealth.com">Contact Us</a></li>
                <li><Link to="/policy">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions">Terms of Service</Link></li>
                <li><Link to="/provider-login">For Providers</Link></li>
              </ul>
            </nav>
          </div>
          <div className="landing-footer-bottom">
            <div>© {new Date().getFullYear()} Youro Health, PLLC. All rights reserved.</div>
            <div className="landing-footer-disclaimer">
              Youro Health, PLLC provides medical care through licensed
              providers and is not a substitute for emergency services. If this
              is an emergency, call 911.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
